import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { getInitials } from '@heidi/utils';
import { ChevronRight } from 'lucide-react';
import { useIntl } from 'react-intl';

import {
  type JoinedWorkspaceRelation,
  type WorkspaceInvitationView,
} from '../../contracts';
import { sortWorkspacesByName } from '../../data/query/useHydrateWorkspaceId';
import { WorkspaceAvatar } from '../workspace/workspace-avatar';
import { workspaceSelectorMessages } from './workspace-selector.messages';

export interface WorkspaceSelectorProps {
  /** The workspaces the signed-in user belongs to; rendered alphabetically. */
  workspaces: JoinedWorkspaceRelation[];
  onSelect: (workspaceId: string) => void;
  /** Pending invitations, listed under the workspaces with a "Join team" action. */
  invitations?: WorkspaceInvitationView[];
  onJoin?: (invitation: WorkspaceInvitationView) => void;
  /**
   * The invitation being joined: `joining` disables its button and reads
   * "Joining…"; `failed` shows an inline error under it.
   */
  joinState?: InvitationJoinState | null;
  /** Given name of the signed-in user; greets them in the heading when present. */
  firstName?: string | null;
  /** Who is signed in, for the footer line. Email may be unknown on some providers. */
  account?: { email?: string | null; displayName?: string | null } | null;
  /** Renders the footer with a Sign out button when provided. */
  onSignOut?: () => void;
}

interface InvitationJoinState {
  invitationId: string;
  status: 'joining' | 'failed';
}

/**
 * The full-screen "Where to today?" surface a multi-workspace user sees after
 * signing in, before the app mounts. One button per workspace, plus an
 * opt-in footer naming who is signed in with a way to sign out.
 *
 * The footer only renders when `onSignOut` is supplied, so selecting a
 * workspace stays the only way off this surface until a caller opts in.
 *
 * It decides nothing. WHEN it shows comes from `useWorkspaceHydrationStatus`
 * (`@heidi/workspace/data`); what selecting (and signing out) does stays
 * app-side in `WorkspaceHydrationGate`, which is what lets it mount above
 * `UserResolver` with only an `IntlProvider` above it — the same contract as
 * `WorkspaceHydrationError`.
 *
 * Rows come from `sortWorkspacesByName`, the same ordering
 * `pickDefaultWorkspaceId` takes its head from, so the workspace the app would
 * have picked on its own is always the first row.
 */
export const WorkspaceSelector = ({
  workspaces,
  onSelect,
  invitations = [],
  onJoin,
  joinState = null,
  firstName = null,
  account = null,
  onSignOut,
}: WorkspaceSelectorProps) => {
  const intl = useIntl();
  const sortedWorkspaces = sortWorkspacesByName(workspaces);
  const signedInLabel = account?.email
    ? intl.formatMessage(workspaceSelectorMessages.signedInAs, { email: account.email })
    : (account?.displayName ?? null);

  return (
    <div
      className="flex h-dvh w-full flex-col overflow-hidden bg-surface-page"
      data-testid="workspace-selector"
    >
      {/* `min-h-0` on both this wrapper and `main` overrides the flex default
          of sizing a flex item to its content, which is what lets `main`
          actually shrink and scroll internally instead of growing past the
          fixed-height shell above — `body` has `overflow: hidden` app-wide, so
          with no internal scroll region a tall list is simply unreachable. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <main className="flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto px-6 py-12">
          {/* `md:my-auto` centers vertically from `md:` up — but as an AUTO
              MARGIN, not `justify-center`. `justify-center` on an overflowing
              scroll container centers the oversized content on both axes,
              which pushes its start (the heading) above `scrollTop: 0` with
              no way to scroll up to it — unreachable, not just off-screen.
              An auto margin has no such trap: the browser can only ever
              collapse it to 0, so a list long enough to overflow just
              degrades to top-aligned and fully reachable by scrolling,
              whatever its length turns out to be. */}
          <div className="flex w-full max-w-xl flex-col gap-8 md:my-auto">
            <div className="flex flex-col gap-2">
              <HeidiLogoMark
                artwork="standard"
                className="mb-4 size-10"
                data-testid="workspace-selector-mark"
              />
              <TypographyH3>
                {firstName
                  ? intl.formatMessage(workspaceSelectorMessages.greetingWithName, {
                      name: firstName,
                    })
                  : intl.formatMessage(workspaceSelectorMessages.heading)}
              </TypographyH3>
              <TypographyP2 className="text-text-secondary">
                {intl.formatMessage(workspaceSelectorMessages.subheading)}
              </TypographyP2>
            </div>

            <ul
              className="flex flex-col gap-3"
              aria-label={intl.formatMessage(
                workspaceSelectorMessages.workspaceListLabel
              )}
            >
              {sortedWorkspaces.map((workspace) => (
                <li key={workspace.workspace_id}>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    disableAutomaticTypographyClasses
                    className="h-auto w-full justify-start gap-4 rounded-xl p-4 text-start font-normal"
                    onClick={() => onSelect(workspace.workspace_id)}
                    data-testid={`workspace-selector-item-${workspace.workspace_id}`}
                  >
                    <WorkspaceAvatar
                      id={workspace.workspace_id}
                      name={workspace.workspace_name}
                      iconUrl={workspace.icon_url}
                    />
                    <TypographyP2Bold className="min-w-0 flex-1 truncate text-text-primary">
                      {workspace.workspace_name}
                    </TypographyP2Bold>
                    <ChevronRight
                      className="size-5 shrink-0 text-text-secondary rtl:-scale-x-100"
                      aria-hidden
                    />
                  </Button>
                </li>
              ))}
            </ul>

            {invitations.length > 0 && onJoin && (
              <ul
                className="flex flex-col gap-3"
                aria-label={intl.formatMessage(
                  workspaceSelectorMessages.invitationListLabel
                )}
              >
                {invitations.map((invitation) => (
                  <InvitationRow
                    key={invitation.invitation_id}
                    invitation={invitation}
                    onJoin={onJoin}
                    status={
                      joinState?.invitationId === invitation.invitation_id
                        ? joinState.status
                        : null
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        </main>

        {onSignOut && (
          // Sibling of the scrolling `main`, not a child — `absolute bottom-0`
          // here anchors to this non-scrolling `relative` wrapper's box, i.e.
          // the visible viewport edge, rather than to `main`'s full (scrolled)
          // content height. Pinned in place while `main` scrolls behind it.
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-surface-page to-transparent"
            aria-hidden
          />
        )}
      </div>

      {onSignOut && (
        <footer className="flex w-full justify-center p-6">
          <div className="flex w-full max-w-xl flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <Avatar
                size="sm"
                color={getAvatarColor(
                  account?.email ?? account?.displayName ?? 'account'
                )}
              >
                <AvatarFallback>
                  {getInitials(account?.displayName ?? account?.email)}
                </AvatarFallback>
              </Avatar>
              {signedInLabel && (
                <TypographyP2 className="min-w-0 truncate text-text-secondary">
                  {signedInLabel}
                </TypographyP2>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={onSignOut}
              data-testid="workspace-selector-sign-out"
              // `sm`'s recipe (`button.styles.ts`) from `lg:` (1024px) up: a 40px
              // touch target is right on phone/tablet, oversized once there's
              // desktop pointer precision and room either side of it.
              className="lg:h-8 lg:min-w-8 lg:gap-x-1 lg:rounded-md lg:px-2.5 lg:py-[9px] lg:text-sm lg:leading-8"
            >
              {intl.formatMessage(workspaceSelectorMessages.signOut)}
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
};

const InvitationRow = ({
  invitation,
  onJoin,
  status,
}: {
  invitation: WorkspaceInvitationView;
  onJoin: (invitation: WorkspaceInvitationView) => void;
  status: InvitationJoinState['status'] | null;
}) => {
  const intl = useIntl();
  const isJoining = status === 'joining';

  return (
    <li
      className="flex flex-col gap-2 rounded-xl border border-outline bg-surface p-4"
      data-testid={`workspace-selector-invite-${invitation.invitation_id}`}
    >
      <div className="flex items-center gap-4">
        <WorkspaceAvatar
          id={invitation.workspace_id ?? invitation.team_id}
          name={invitation.workspace_name}
          iconUrl={invitation.icon_url}
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <TypographyP2Bold className="truncate text-text-primary">
            {invitation.workspace_name}
          </TypographyP2Bold>
          <TypographyCaption className="truncate text-text-secondary">
            {intl.formatMessage(workspaceSelectorMessages.invitationCaption)}
          </TypographyCaption>
        </span>
        <Button
          type="button"
          size="sm"
          disabled={isJoining}
          onClick={() => onJoin(invitation)}
          data-testid={`workspace-selector-join-${invitation.invitation_id}`}
        >
          {intl.formatMessage(
            isJoining
              ? workspaceSelectorMessages.joining
              : workspaceSelectorMessages.joinTeam
          )}
        </Button>
      </div>
      {status === 'failed' && (
        <TypographyCaption role="alert" className="text-validation-destructive">
          {intl.formatMessage(workspaceSelectorMessages.joinFailed)}
        </TypographyCaption>
      )}
    </li>
  );
};
